import * as React from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router';
import BakeasyList from '../common/BakeasyList';
import {
  Logout,
  Support,
  Store,
  SettingsOutlined,
  LockOutlined,
  Message,
  Translate,
} from '@mui/icons-material';
import { Card, Grid, Typography, useMediaQuery } from '@mui/material';
import orderezlogo from '../../assets/orderezlogo.jpg';
import { clearCookies } from '../../utils/CookieUtils';
import i18n from '../../languages/languages';
import { t } from 'i18next';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import { authActions } from '../../redux_store/slices/authSlice';
import { clearSupplierState } from '../../redux_store/slices/suppliersSlice';
import { clearStoresState } from '../../redux_store/slices/storesSlice';
import { AppContext } from '../../context/AppContext';
import { BakeasySelectLabelProfile } from '../dropdowns/BakeasySelect';
import { BLUE_GRAY_500 } from '../../constants/Colors';
import { modifyLanguage } from '../../redux_store/slices/languagesSlice';
import { useState } from 'react';
import {
  getProfile,
  getProfileState,
} from '../../redux_store/slices/GetUserProfileSlice';
import {
  clearMessagesState,
  fetchNewMessageCount,
} from '../../redux_store/slices/messagesSlice';
import { findLanguageCode } from '../../languages/LanguageUtils';

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: { width: `calc(${theme.spacing(8)} + 1px)` },
});

const FireNav = styled(List)<{ component?: React.ElementType }>({
  '& .MuiListItemIcon-root': { minWidth: 0 },
  '& .MuiSvgIcon-root': { fontSize: 20 },
});

const Backdrop = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: theme.zIndex.drawer + 1,
  display: 'none',
}));

export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

export const DrawerHeaderIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
      marginLeft: drawerWidth,
      width: `calc(100% - ${drawerWidth}px)`,
      transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
    ...(isMobile && {
      position: 'fixed',
      zIndex: theme.zIndex.modal + 1,
      ...(open && { width: '100%' }),
    }),
  };
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(isMobile && {
      position: 'fixed',
      zIndex: theme.zIndex.modal + 1,
      ...(open && { width: '100%' }),
      ...(!open && { display: 'none' }),
    }),
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  };
});

export interface MenuItemProps {
  key?: number;
  id: number;
  lable: string;
  icon: any;
  isSelected: boolean;
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void;
}

interface BakeasyNavigationDrawer {
  onDrawerStateChange?: (open: boolean) => void;
}

export default function BakeasyNavigationDrawer({
  onDrawerStateChange,
}: BakeasyNavigationDrawer) {
  // Close the drawer only in mobile view
  const handleMenuItemClick = (route: string) => {
    navigate(route);

    if (window.innerWidth <= theme.breakpoints.values.sm) {
      handleDrawerClose();
    }
  };

  const menuItemsList: MenuItemProps[] = [
    {
      id: 1,
      lable: 'SUPPLIERS',
      isSelected: false,
      icon: <Support />,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(clearSupplierState());
        handleMenuItemClick('/suppliers');
      },
    },
    {
      id: 2,
      lable: 'STORES',
      icon: <Store />,
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(clearStoresState());
        handleMenuItemClick('/stores');
      },
    },
    // {
    // 	id: 3,
    // 	lable: "PRODUCTS",
    // 	icon: <ProductionQuantityLimits />,
    // 	isSelected: false,
    // 	onClick: (event: React.MouseEvent<HTMLDivElement>) => {
    // 		dispatch(clearProductState());
    // 		handleMenuItemClick("/products");
    // 	},
    // },
    {
      id: 4,
      lable: 'MESSAGES',
      icon: <Message />,
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        dispatch(fetchNewMessageCount(''));
        dispatch(clearMessagesState());
        handleMenuItemClick('/messages');
      },
    },
  ];

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = React.useState(!isMobile);
  const navigate = useNavigate();
  const [toolbarOpen, setToolbarOpen] = React.useState(false);
  const [assignedRoles, setAssignedRoles] = React.useState('');
  const [backdropVisible, setBackdropVisible] = useState(false);
  const [defaultLanguage, setDefaultLanguage] = useState('');
  let myContext = React.useContext(AppContext);

  let _userProfile: any = useAppSelector(getProfileState);

  const handleDrawerOpen = () => {
    setOpen(true);
    setBackdropVisible(true); // Show the backdrop when drawer opens
    document.body.style.overflow = 'hidden'; // Disable body scroll
    onDrawerStateChange?.(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setBackdropVisible(false); // Hide the backdrop when drawer closes
    document.body.style.overflow = 'auto'; // Re-enable body scroll
    onDrawerStateChange?.(false);
  };

  const LogOut = () => {
    clearCookies();
    return dispatch(authActions.logout());
  };

  const profileMenuList: MenuItemProps[] = [
    {
      id: 1,
      lable: 'PROFILE',
      isSelected: false,
      icon: (
        <SettingsOutlined
          sx={{ fontSize: '1.25rem', marginRight: 1, color: BLUE_GRAY_500 }}
        />
      ),
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        handleMenuItemClick('/profile');
      },
    },
    {
      id: 2,
      lable: 'LANGUAGE',
      icon: (
        <Translate
          sx={{ fontSize: '1.25rem', marginRight: 1, color: BLUE_GRAY_500 }}
        />
      ),
      isSelected: false,
      onClick: async (event: React.MouseEvent<HTMLDivElement>) => {
        await dispatch(getProfile(''));
        openLanguageConfirmDialog();
      },
    },
    {
      id: 3,
      lable: 'MASTER_PASSWORD',
      icon: (
        <LockOutlined
          sx={{ fontSize: '1.25rem', marginRight: 1, color: BLUE_GRAY_500 }}
        />
      ),
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => {
        handleMenuItemClick('/master-password');
      },
    },
    {
      id: 4,
      lable: 'LOGOUT',
      icon: (
        <Logout
          sx={{ fontSize: '1.25rem', marginRight: 1, color: BLUE_GRAY_500 }}
        />
      ),
      isSelected: false,
      onClick: (event: React.MouseEvent<HTMLDivElement>) => LogOut(),
    },
  ];

  function openLanguageConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'language-Dialog',
      open: true,
      title: t('SELECT_LANGUAGE'),
      onYesClick: async (e: any, newLanguage) => {
        await dispatch(modifyLanguage({ language: newLanguage }));
        await dispatch(getProfile(''));
        changeLanguage(newLanguage);
      },
    });
  }

  function changeLanguage(language: any) {
    setDefaultLanguage(language);
    window.location.reload();
  }

  function getAndSetMenus() {
    let roles: string = t('BAKEASY_ADMIN_PORTAL');
    setAssignedRoles(roles);
  }

  React.useEffect(() => {
    let temp = _userProfile?.value?.defaultLanguage;
    i18n.changeLanguage(findLanguageCode(temp));
    getAndSetMenus();
  }, [_userProfile, defaultLanguage]);

  React.useEffect(() => {
    // Ensure the drawer is properly opened or closed based on screen size
    if (window.innerWidth <= theme.breakpoints.values.sm) {
      setOpen(false);
    } else {
      setOpen(true);
    }

    function handleResize() {
      if (window.innerWidth <= theme.breakpoints.values.sm) {
        setOpen(false);
        setBackdropVisible(false);
        document.body.style.overflow = 'auto';
      } else {
        setOpen(true);
      }
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [theme.breakpoints.values.sm]);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar style={{ backgroundColor: 'white' }}>
          <IconButton
            color="primary"
            aria-label="open drawer"
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            edge="start"
            sx={{ marginRight: 5, fontSize: 20 }}
          >
            {<MenuIcon />}
          </IconButton>
          <Typography sx={{ flexGrow: 1 }}>{assignedRoles}</Typography>

          <Grid>
            <BakeasySelectLabelProfile
              options={profileMenuList}
              value={''}
              onChange={(event: any) => { }}
            />
          </Grid>
        </Toolbar>
        {toolbarOpen && (
          <Card sx={{ position: 'absolute', right: '25px', top: '50px' }}>
            <BakeasyList
              items={profileMenuList}
              dense={true}
              onMouseOver={(event: React.MouseEvent<HTMLDivElement>) => {
                setToolbarOpen(true);
              }}
              onMouseOut={(event: React.MouseEvent<HTMLDivElement>) => {
                setToolbarOpen(false);
              }}
            />
          </Card>
        )}
      </AppBar>

      <Drawer
        variant={isMobile && !open ? 'temporary' : 'permanent'}
        open={isMobile || open}
        onClose={handleDrawerClose}
        sx={{
          width: open ? drawerWidth : theme.mixins.toolbar.minHeight,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          ...(isMobile && {
            position: 'fixed',
            zIndex: theme.zIndex.drawer + 1,
            width: drawerWidth,
            marginTop: 90,
            top: 90,
            bottom: 0,
          }),
          ...(!isMobile && { marginTop: theme.mixins.toolbar.minHeight }),
        }}
      >
        {isMobile ? (
          <Box component="main" sx={{ position: 'absolute' }}>
            <DrawerHeader>
              <img
                src={orderezlogo}
                height="50px"
                style={{ cursor: 'pointer' }}
              />
            </DrawerHeader>
          </Box>
        ) : (
          <DrawerHeader>
            <img
              src={orderezlogo}
              height="45px"
              style={{ cursor: 'pointer' }}
            />
          </DrawerHeader>
        )}
        <Divider />
        <FireNav component="nav" sx={isMobile ? { marginTop: 6 } : {}}>
          <BakeasyList items={menuItemsList} />
        </FireNav>
      </Drawer>

      {/* Backdrop - blocks interactions when the drawer is open */}
      {backdropVisible && <Backdrop onClick={handleDrawerClose} />}
    </Box>
  );
}
